import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { TrialService } from '../services/trialService';

interface TrialActivationCardProps {
  webUserId: string;
  onTrialActivated: () => void;
}

export default function TrialActivationCard({
  webUserId,
  onTrialActivated
}: TrialActivationCardProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleActivateTrial = async () => {
    setLoading(true);
    setError('');

    try {
      // Check if trial can be activated
      const canActivate = await TrialService.canActivateTrial(webUserId);

      if (!canActivate) {
        setError('Trial cannot be activated at this time.');
        return;
      }

      // Activate trial
      await TrialService.activateTrial(webUserId);

      // Notify parent component
      onTrialActivated();

    } catch (error: any) {
      console.error('Error activating trial:', error);
      setError(error.message || 'Failed to activate trial. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      sx={{
        border: '2px dashed rgba(255, 255, 255, 0.2)',
        borderRadius: '16px',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        position: 'relative',
        overflow: 'visible',
        color: '#ffffff'
      }}
    >
      <Chip
        label="FREE TRIAL"
        icon={<SecurityIcon />}
        sx={{
          position: 'absolute',
          top: -12,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #4A90C2, #2E86AB)',
          color: 'white',
          fontWeight: 600,
          fontSize: '0.875rem'
        }}
      />

      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
            🎁 Free Trial Ready!
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.8)' }}>
            Start your 7-day free trial now
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 40, color: '#4A90C2', mb: 1 }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff' }}>
              7 Full Days
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <PlayArrowIcon sx={{ fontSize: 40, color: '#10b981', mb: 1 }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff' }}>
              Instant Access
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <SecurityIcon sx={{ fontSize: 40, color: '#2E86AB', mb: 1 }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff' }}>
              No Payment
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleActivateTrial}
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #4A90C2, #2E86AB)',
            color: 'white',
            fontWeight: 600,
            py: 2,
            px: 4,
            borderRadius: '12px',
            fontSize: '1.1rem',
            mb: 3,
            '&:hover': {
              background: 'linear-gradient(135deg, #2E86AB, #1a5f7a)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(74, 144, 194, 0.4)'
            },
            '&:disabled': {
              background: 'rgba(74, 144, 194, 0.3)',
              color: 'rgba(255, 255, 255, 0.6)'
            }
          }}
        >
          {loading ? 'Activating Trial...' : 'Start 7-Day Trial'}
        </Button>

        <Alert
          severity="warning"
          sx={{
            mt: 2,
            backgroundColor: 'rgba(74, 144, 194, 0.1)',
            border: '1px solid rgba(74, 144, 194, 0.3)',
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#4A90C2'
            }
          }}
        >
          <Typography variant="body2">
            <strong>⚠️ Hotel code is currently INACTIVE</strong>
            <br />
            Your multizone player apps cannot connect until trial is activated
          </Typography>
        </Alert>

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            💡 <strong>What you get:</strong><br />
            • Unlimited zones and scheduling<br />
            • 7 days of full access<br />
            • No credit card required<br />
            • Cancel anytime
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}